import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Modal } from "bootstrap";
import { createPortal } from "react-dom";

const API_KEY_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

function generateApiKey32() {
  const buf = new Uint8Array(32);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => API_KEY_CHARS[b % API_KEY_CHARS.length]).join(
    "",
  );
}

export default function MakeAgentModal({ agent, show, onClose, type, modalId }) {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [prefixInputs, setPrefixInputs] = useState(["", "", "", "", ""]);
  const [isDuplicate, setIsDuplicate] = useState(null); // null | true | false
  const [checkMsg, setCheckMsg] = useState("");
  const [newUserId, setNewUserId] = useState("");
  const [whiteList, setWhiteList] = useState("");
  const [callBackUrl, setCallBackUrl] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("NORMAL");
  const inputRefs = useRef([]);
  const modalRef = useRef(null);
  const modalInstance = useRef(null);
  const [rate, setRate] = useState("");
  const [apiKey, setApiKey] = useState("");

  useEffect(() => {
    if (show && (type === 0 || type === 2)) {
      setApiKey("");
    }
  }, [show, type]);

  useEffect(() => {
    if (modalRef.current && show) {
      modalInstance.current = new Modal(modalRef.current, {
        backdrop: "static",
        keyboard: true,
      });
      modalInstance.current.show();

      const handleHidden = () => {
        document.body.classList.remove("modal-open");
        const backdrop = document.querySelector(".modal-backdrop");
        if (backdrop) backdrop.remove();
        document.body.style.overflow = "auto";
        if (onClose) onClose();
      };

      modalRef.current.addEventListener("hidden.bs.modal", handleHidden);

      return () => {
        modalRef.current?.removeEventListener("hidden.bs.modal", handleHidden);
        modalInstance.current?.hide();
      };
    }
  }, [show]);

  useEffect(() => {
    if ((type === 1 || type === 3) && agent?.user_id) {
      const token = localStorage.getItem("token");
      axios
        .get(`${API_BASE}/api/AgentInfo`, {
          params: { user_id: agent.user_id },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const {
            user_id,
            prefix,
            white_list,
            api_url,
            user_pw,
            status,
            rate,
            api_key,
          } = res.data;

          setNewUserId(user_id);
          setPrefixInputs(prefix.split("").slice(0, 5));
          setIsDuplicate(false);
          setCallBackUrl(api_url);
          setWhiteList(white_list);
          setRate(rate ?? "");
          setApiKey(api_key ?? "");

          if (type == 2 || type == 3) {
            setPassword(user_pw);
            setConfirm(user_pw);
            setStatus(status);
          }
        })
        .catch((err) => {
          console.error("유저 정보 불러오기 실패:", err);
        });
    }
  }, [type, agent]);

  const handlePrefixChange = (index, value) => {
    if (!/^[a-zA-Z0-9]*$/.test(value)) return; // 영문, 숫자만 허용
    const updated = [...prefixInputs];
    updated[index] = value.toUpperCase();
    setPrefixInputs(updated);
    setIsDuplicate(null); // 입력 바뀌면 초기화
    setCheckMsg("");
    if (value.length === 1 && index < 4) {
      inputRefs.current[index + 1]?.focus(); // 다음 칸으로 자동 이동
    }
  };

  const isFormValid = () => {
    return (
      isDuplicate === false && isPasswordValid(password) && password === confirm
    );
  };

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    const prefix = prefixInputs.join("");

    try {
      const url =
        type === 0 || type === 2
          ? `${API_BASE}/api/Create`
          : `${API_BASE}/api/Update`;

      const payload =
        type === 0 || type === 2
          ? {
            prefix,
            password,
            user_id: newUserId,
            agent_id: agent.user_id,
            white_list: whiteList,
            api_url: callBackUrl,
            status: status,
            rate,
            api_key: apiKey.trim() || null,
          }
          : {
            prefix,
            password,
            user_id: agent.user_id,
            white_list: whiteList,
            api_url: callBackUrl,
            status: status,
            rate,
            api_key: apiKey.trim() || null,
          };

      await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("저장되었습니다.");
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        alert("이미 존재하는 아이디입니다. 다른 아이디를 입력해주세요.");
      } else if (err.response?.status === 400) {
        alert("입력 정보가 부족합니다.");
      } else {
        console.error("저장 오류:", err);
        alert("저장 중 서버 오류가 발생했습니다.");
      }
    }
  };
  const handleRateChange = (e) => {
    const value = e.target.value;

    // 숫자 + 소수점 허용 (예: 12, 12.5, 0.25)
    if (/^\d*\.?\d*$/.test(value)) {
      setRate(value);
    }
  };

  const isPasswordValid = (pw) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[\d\W]).{8,}$/;
    return regex.test(pw);
  };

  const checkPrefix = async () => {
    const prefix = prefixInputs.join("");
    if (prefix.length !== 5) {
      setCheckMsg("5글자 모두 입력해주세요.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/CheckPrefix`, {
        params: { prefix: prefix },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data.exists) {
        setIsDuplicate(true);
        setCheckMsg("❌ 이미 사용중인 Prefix입니다.");
      } else {
        setIsDuplicate(false);
        setCheckMsg("✅ 사용 가능한 Prefix입니다.");
      }
    } catch (err) {
      console.error("Prefix 확인 오류:", err);
      setCheckMsg("⚠️ 서버 오류 발생");
    }
  };

  return createPortal(
    <div
      className="modal fade"
      tabIndex="-1"
      ref={modalRef}
      id={modalId || "ModalInputMadeUser"}
    >
      <div
        className="modal-dialog modal-dialog-scrollable modal-lg"
        data-v-f31faa6f=""
        role="document"
        style={{ maxHeight: "calc(100vh - 1rem)" }}
      >
        <div
          className="modal-content d-flex flex-column"
          data-v-f31faa6f=""
          style={{ maxHeight: "min(90vh, 100%)" }}
        >
          <div className="modal-header flex-shrink-0" data-v-f31faa6f="">
            <h5 className="modal-title" data-v-f31faa6f="">
              {agent.user_id} 님{" "}
              {type == 0 || type == 2 ? "하부 그룹 생성" : "정보 수정"}
            </h5>
            <a
              data-v-f31faa6f=""
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onClose();
              }}
            >
              <em className="icon ni ni-cross" data-v-f31faa6f="" />
            </a>
          </div>
          <div
            className="modal-body modal-body-lg flex-grow-1 overflow-auto"
            data-v-f31faa6f=""
            style={{ minHeight: 0 }}
          >
            <form
              action="#"
              className="form-validate is-alter"
              data-v-f31faa6f=""
              noValidate
            >
              <div className="row gy-3" data-v-f31faa6f="">
                <div className="col-lg-12 col-sm-12" data-v-f31faa6f="">
                  <div
                    className="form-label-outlined"
                    data-v-f31faa6f=""
                    htmlFor="user_prefix"
                    style={{
                      marginLeft: "23px",
                    }}
                  >
                    {" "}
                    Prefix(접두어){" "}
                    <sup className="text-pink" data-v-f31faa6f="">
                      5글자 영문, 숫자만 입력가능합니다
                    </sup>
                  </div>
                  {prefixInputs.map((char, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      className="ms-1 form-control form-control-lg text-center inputs"
                      maxLength="1"
                      disabled={type === 1 || type === 3}
                      style={{ display: "inline-block", maxWidth: "50px" }}
                      type="text"
                      value={char}
                      onChange={(e) => handlePrefixChange(idx, e.target.value)}
                    />
                  ))}

                  {type === 0 ||
                    (type === 2 && (
                      <>
                        <div
                          className="ms-1 btn btn-primary btn-lg"
                          onClick={checkPrefix}
                        >
                          <i className="fas fa-check" data-v-f31faa6f="" />
                          중복확인
                        </div>

                        {isDuplicate === null ? (
                          <div className="text-danger" data-v-f31faa6f="">
                            Prefix 입력후 중복확인
                          </div>
                        ) : (
                          <div
                            className={
                              isDuplicate ? "text-danger" : "text-success"
                            }
                            data-v-f31faa6f=""
                          >
                            {checkMsg}
                          </div>
                        )}
                      </>
                    ))}
                </div>
                <div className="col-6 col-sm-6" data-v-f31faa6f="">
                  <div className="form-group" data-v-f31faa6f="">
                    <label className="" data-v-f31faa6f="" htmlFor="username">
                      로그인아이디
                    </label>
                    <div className="form-control-wrap" data-v-f31faa6f="">
                      <input
                        className="form-control form-control-lg form-control-outlined"
                        data-v-f31faa6f=""
                        id="username"
                        type="text"
                        disabled={type === 1 || type === 3}
                        value={newUserId || ""}
                        onChange={(e) => setNewUserId(e.target.value)}
                      />
                      <sup className="text-danger" data-v-f31faa6f="">
                        영문+영문/숫자 3자이상
                      </sup>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-sm-6" data-v-f31faa6f=""></div>
                <div className="col-lg-6 col-sm-6" data-v-f31faa6f="">
                  <div className="form-group" data-v-f31faa6f="">
                    <label className="" data-v-f31faa6f="" htmlFor="password">
                      비밀번호
                    </label>
                    <div className="form-control-wrap" data-v-f31faa6f="">
                      <div
                        className="form-icon form-icon-right"
                        data-v-f31faa6f=""
                      >
                        <em className="icon ni ni-lock" data-v-f31faa6f="" />
                      </div>
                      <input
                        className="form-control form-control-lg form-control-outlined"
                        data-v-f31faa6f=""
                        id="password"
                        type="text"
                        value={password || ""}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      {password && !isPasswordValid(password) && (
                        <div data-v-f31faa6f="" className="form-note invalid">
                          비밀번호의 규격이 맞지않습니다.
                        </div>
                      )}
                      <sup className="text-danger" data-v-f31faa6f="">
                        영문 + 대문자 영문 + 특수문자/ 숫자 8자리이상
                      </sup>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6" data-v-f31faa6f="">
                  <div className="form-group" data-v-f31faa6f="">
                    <label
                      className=""
                      data-v-f31faa6f=""
                      htmlFor="password_confirm"
                    >
                      비밀번호 확인
                    </label>
                    <div className="form-control-wrap" data-v-f31faa6f="">
                      <div
                        className="form-icon form-icon-right"
                        data-v-f31faa6f=""
                      >
                        <em
                          className="icon ni ni-lock-alt"
                          data-v-f31faa6f=""
                        />
                      </div>
                      <input
                        className="form-control form-control-lg form-control-outlined"
                        data-v-f31faa6f=""
                        id="password_confirm"
                        type="text"
                        value={confirm || ""}
                        onChange={(e) => setConfirm(e.target.value)}
                      />
                      {confirm && confirm !== password && (
                        <div data-v-f31faa6f="" className="form-note invalid">
                          비밀번호가 다릅니다.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-12" data-v-f31faa6f="">
                  <div className="form-group" data-v-f31faa6f="">
                    <label className="" data-v-f31faa6f="" htmlFor="rate">
                      알값(요율)
                    </label>
                    <div className="form-control-wrap" data-v-f31faa6f="">
                      <input
                        className="form-control form-control-lg form-control-outlined"
                        id="rate"
                        type="text"
                        value={rate}
                        //TODO 내하위는 수정해야하고 본인은 수정안되야함
                        disabled={type !== 2 && type !== 3}
                        onChange={handleRateChange}
                        placeholder="예: 12.58"
                      />
                    </div>
                    <div className="form-note" data-v-f31faa6f="">
                      숫자 또는 소수점만 입력 가능합니다.
                    </div>
                  </div>
                </div>

                <div className="col-lg-12 col-sm-12" data-v-f31faa6f="">
                  <div className="form-group" data-v-f31faa6f="">
                    <label className="" data-v-f31faa6f="" htmlFor="api_key">
                      API Key
                    </label>
                    <div className="form-control-wrap" data-v-f31faa6f="">
                      <div
                        className="input-group"
                        style={{ flexWrap: "nowrap" }}
                      >
                        <input
                          className="form-control form-control-lg form-control-outlined"
                          id="api_key"
                          type="text"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="32자 권장 · 직접 입력 또는 발급"
                          disabled={type === 1}
                          autoComplete="off"
                          spellCheck={false}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          disabled={type === 1}
                          onClick={() => setApiKey(generateApiKey32())}
                        >
                          발급
                        </button>
                      </div>
                    </div>
                    <div className="form-note" data-v-f31faa6f="">
                      영문·숫자 조합 32자 랜덤 발급 또는 원하는 값을 직접 입력할 수
                      있습니다.
                    </div>
                  </div>
                </div>

                <div className="col-lg-12 col-sm-12" data-v-f31faa6f="">
                  <div className="form-group" data-v-f31faa6f="">
                    <label className="" data-v-f31faa6f="" htmlFor="currency">
                      지갑 형식
                    </label>
                    <div className="form-control-wrap" data-v-f31faa6f="">
                      <select
                        aria-hidden="true"
                        className="form-select js-select2"
                        data-select2-id="currency"
                        data-v-f31faa6f=""
                        disabled
                        id="currency"
                        tabIndex="0"
                      >
                        <option
                          data-select2-id="72"
                          data-v-f31faa6f=""
                          value="y"
                        >
                          심리스
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                {type === 3 || type === 2 ? (
                  <>
                    <div className="col-lg-12 col-sm-12" data-v-f31faa6f="">
                      <div className="form-group" data-v-f31faa6f="">
                        <label
                          className=""
                          data-v-f31faa6f=""
                          htmlFor="currency"
                        >
                          상태
                        </label>
                        <div className="form-control-wrap" data-v-f31faa6f="">
                          <select
                            aria-hidden="true"
                            className="form-select js-select2"
                            id="status"
                            tabIndex="0"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                          >
                            <option value="NORMAL">정상</option>
                            <option value="STOP">정지</option>
                            <option value="PENDING">승인대기</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-12 col-sm-12" data-v-f31faa6f="">
                      <div className="form-group" data-v-f31faa6f="">
                        <label
                          className=""
                          data-v-f31faa6f=""
                          htmlFor="currency"
                        >
                          화이트 아이피
                        </label>
                        <div className="form-control-wrap" data-v-f31faa6f="">
                          <input
                            className="form-control form-control-lg form-control-outlined"
                            data-v-f31faa6f=""
                            id="username"
                            type="text"
                            value={whiteList || ""}
                            onChange={(e) => setWhiteList(e.target.value)}
                          />
                        </div>
                        <div data-v-f31faa6f="" className="form-note invalid">
                          ,를 사용해서 구분해야합니다.
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-12 col-sm-12" data-v-f31faa6f="">
                      <div className="form-group" data-v-f31faa6f="">
                        <label
                          className=""
                          data-v-f31faa6f=""
                          htmlFor="currency"
                        >
                          콜백 URL
                        </label>
                        <div className="form-control-wrap" data-v-f31faa6f="">
                          <input
                            className="form-control form-control-lg form-control-outlined"
                            data-v-f31faa6f=""
                            id="username"
                            type="text"
                            value={callBackUrl}
                            onChange={(e) => setCallBackUrl(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
                <div className="col-12" data-v-f31faa6f="">
                  <div className="form-group" data-v-f31faa6f="">
                    <button
                      className="btn btn-lg btn-primary me-1"
                      data-v-f31faa6f=""
                      type="button"
                      disabled={!isFormValid()}
                      onClick={handleSave}
                    >
                      <i className="fas fa-save" data-v-f31faa6f="" />
                      저장(수정){" "}
                    </button>
                    <button
                      className="btn btn-lg btn-outline-gray me-1"
                      data-v-f31faa6f=""
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        onClose();
                      }}
                    >
                      <i className="fas fa-times-circle" data-v-f31faa6f="" />
                      닫기
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
